import { colors } from '@lmc/common/constants'
import { ConsentStatus, getIsSignStatus } from '@lmc/common/types'
import type {
  ConsentForm,
  ContingencyPlanDTO,
  Home,
  LogCategoryItem,
  Resident
} from '@lmc/ts-common'
import {
  Icon,
  LabelIcon,
  NextReviewField,
  PageHeader,
  Tooltip
} from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { Form, Row } from 'antd'
import ConsentStatusTag from 'components/consent/ConsentStatusTag'
import { CarePlanSelector } from 'components/generic'
import type { CarePlanAdlExtended } from 'components/generic/CarePlanSelector/CarePlanSelector'
import { ContingencyPlanSelector } from 'components/generic/ContingencyPlanSelector/ContingencyPlanSelector'
import { RiskAssessmentSelector } from 'components/generic/RiskAssessmentSelector/RiskAssessmentSelector'
import SignatureModal from 'components/generic/SignatureSection/SignatureModal'
import PdfExport from 'components/pdf/PdfExport'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { consentConstants } from 'locale/consent'
import type * as React from 'react'
import {
  type CSSProperties,
  type ReactElement,
  useEffect,
  useRef,
  useState
} from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import type { ConsentFormSnapshot } from 'screens/CarePlans/hooks/useCarePlanPopulated'
import styled from 'styled-components'
import type { PopulatedCarePlan } from 'types/shared'
import {
  ConsentConfirmationCard,
  getIsConsentConfirmDisabled,
  getNextReviewFieldState
} from '../ResidentConsentFormsScreen/components'
import { getTimeOptions, onSubmit, setNextReviewDateByOptions } from '../utils'
import {
  CarePlanLinkingConfirmationModal,
  ConsentFormTextBox,
  LogsSection,
  NextReviewCard
} from './components'
import PDFHeader from './PDFScreen/PDFHeader'

type CarePlanItem = CarePlanAdlExtended | PopulatedCarePlan | string
type ContingencyPlanItem = ContingencyPlanDTO | string

const extractCarePlanIds = (carePlans: CarePlanItem[]): string[] =>
  carePlans.map((cp) => (typeof cp === 'string' ? cp : cp.id))

const extractContingencyPlanIds = (plans: ContingencyPlanItem[]): string[] =>
  plans.map((plan) => (typeof plan === 'string' ? plan : plan.id))

type ConsentFormScreenProps = {
  fetchResidentConsentFormByID: (id: string) => void
  consentForm: ConsentForm
  home: Home
  printPDF?: boolean
  resident: Resident
  router: any
  logs: any
  signatureItem: LogCategoryItem
  updateConsent: ({
    isRefused,
    comment,
    editedBy,
    editedOn,
    nextReviewDate,
    consentForm
  }: any) => Promise<any>
}

export const ConsentFormScreen = ({
  consentForm,
  fetchResidentConsentFormByID,
  home,
  printPDF,
  logs,
  resident,
  router,
  signatureItem,
  updateConsent
}: ConsentFormScreenProps): ReactElement | null => {
  const { t } = useTranslation()
  const featureFlags = useFeatureFlags({
    flagNames: ['consent_future_plan', 'consent_risk_link']
  }).data
  const isConsentFuturePlanEnabled =
    featureFlags['consent_future_plan']?.enabled || false
  const isConsentRiskLinkEnabled =
    featureFlags['consent_risk_link']?.enabled || false
  const translate = (key: string, variable?: any) =>
    t(`consent.createConsentForms.components.consentModal.${key}`, variable)

  // Get carers from Redux store for name lookup
  const carers = useSelector((state: any) => state.data.carers)
  const queryClient = useQueryClient()

  const labelTextBoxTranslate = (key: string, variable?: any) =>
    t(`components.labelTextBox.${key}`, variable)

  const STATUS_TO_OPTION_KEY: Record<ConsentStatus, string> = {
    [ConsentStatus.noStatus]: 'OPTION_NO_STATUS',
    [ConsentStatus.consentNotObtained]: 'OPTION_CONSENT_NOT_OBTAINED',
    [ConsentStatus.completed]: 'OPTION_COMPLETED',
    [ConsentStatus.planned]: 'OPTION_PLANNED',
    [ConsentStatus.requested]: 'OPTION_REQUESTED',
    [ConsentStatus.scheduled]: 'OPTION_SCHEDULED',
    [ConsentStatus.postponed]: 'OPTION_POSTPONED',
    [ConsentStatus.cancelled]: 'OPTION_CANCELLED',
    [ConsentStatus.withdrawn]: 'OPTION_WITHDRAWN',
    [ConsentStatus.refused]: 'OPTION_REFUSED',
    [ConsentStatus.provided]: 'OPTION_PROVIDED',
    [ConsentStatus.pending]: 'OPTION_PLANNED'
  }

  const getStatusLabel = (status: ConsentStatus): string => {
    const optionKey = STATUS_TO_OPTION_KEY[status]
    return optionKey ? t(`consent:${optionKey}`) : status
  }

  const [isReviewing, setIsReviewing] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [nextReviewDate, setNextReviewDate] = useState<string | undefined>()
  const [additionalInfo, setAdditionalInfo] = useState<string | undefined>()
  const [timeOptionNextReview, setTimeOptionNextReview] = useState<
    string | undefined
  >()
  const [selectedCarePlans, setSelectedCarePlans] = useState<
    CarePlanAdlExtended[] | PopulatedCarePlan[] | string[]
  >([])
  const [initialCarePlanIds, setInitialCarePlanIds] = useState<string[]>([])
  const [selectedContingencyPlans, setSelectedContingencyPlans] = useState<
    ContingencyPlanItem[]
  >([])
  const [initialContingencyPlanIds, setInitialContingencyPlanIds] = useState<
    string[]
  >([])
  const [selectedRiskAssessments, setSelectedRiskAssessments] = useState<
    string[]
  >([])
  const [initialRiskAssessmentIds, setInitialRiskAssessmentIds] = useState<
    string[]
  >([])
  const [showSignatureModal, setShowSignatureModal] = useState(false)
  const [showCarePlanLinkingModal, setShowCarePlanLinkingModal] =
    useState(false)
  const [newlyLinkedCarePlans, setNewlyLinkedCarePlans] = useState<
    CarePlanAdlExtended[]
  >([])
  const [newlyLinkedContingencyPlans, setNewlyLinkedContingencyPlans] =
    useState<ContingencyPlanDTO[]>([])

  const navigate = useNavigate()
  const location = useLocation()

  // Check if we have snapshot data from navigation state
  const snapshotData = (location.state as any)?.snapshot as
    | ConsentFormSnapshot
    | undefined
  const isSnapshot = (location.state as any)?.isSnapshot as boolean | undefined
  const from = (location.state as any)?.from as string | undefined

  const [form] = Form.useForm()

  const consentFormIdFromURL = router.params.consent_form_id

  const [consentStatus, setConsentStatus] = useState<{
    status: ConsentStatus
    reason?: string
    lastReviewDate?: string | null
  }>({
    status: ConsentStatus.noStatus
  })
  const isSignStatus = getIsSignStatus(consentStatus.status)

  const isInitialized = useRef(false)

  // Reset initialization flag when consent form ID changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    isInitialized.current = false
  }, [consentFormIdFromURL])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    // Only fetch if we don't have snapshot data
    if (!isSnapshot) {
      fetchResidentConsentFormByID(consentFormIdFromURL)
    }
  }, [consentFormIdFromURL, isSnapshot])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    // @ts-ignore
    setNextReviewDateByOptions(timeOptionNextReview, setNextReviewDate, form)
  }, [timeOptionNextReview])

  // Initialize from snapshot data if available
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isSnapshot && snapshotData && !isInitialized.current) {
      isInitialized.current = true

      // Initialize consentStatus from snapshot
      if (snapshotData.consentStatus) {
        setConsentStatus({
          status: snapshotData.consentStatus as ConsentStatus,
          reason: undefined,
          lastReviewDate: snapshotData.signedOn
            ? new Date(snapshotData.signedOn).toISOString()
            : null
        })
      }

      // Initialize nextReviewDate from snapshot
      if (snapshotData.nextReviewDate) {
        setNextReviewDate(
          dayjs(snapshotData.nextReviewDate) as unknown as string
        )
      }
    }
  }, [isSnapshot, snapshotData])

  // Sync selectedCarePlans from consentForm whenever the API data changes (unless user is editing)
  // consentForm.carePlans is typed as LmcFk[] in ts-common but the API now returns PopulatedCarePlan[]
  const apiCarePlans = consentForm?.carePlans as
    | PopulatedCarePlan[]
    | string[]
    | undefined
  const apiContingencyPlans = (
    consentForm as
      | (ConsentForm & { contingencyPlans?: ContingencyPlanItem[] })
      | undefined
  )?.contingencyPlans
  const apiRiskAssessments = (
    consentForm as (ConsentForm & { riskAssessments?: string[] }) | undefined
  )?.riskAssessments

  const isReviewingRef = useRef(isReviewing)
  isReviewingRef.current = isReviewing

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (apiCarePlans?.length && !isReviewingRef.current && !isSnapshot) {
      setSelectedCarePlans(apiCarePlans)
      setInitialCarePlanIds(extractCarePlanIds(apiCarePlans))
    }
  }, [apiCarePlans, isSnapshot])

  // Keep selectedContingencyPlans aligned with API updates unless the user is actively editing.
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (
      isConsentFuturePlanEnabled &&
      !isReviewingRef.current &&
      !isSnapshot &&
      apiContingencyPlans
    ) {
      setSelectedContingencyPlans(apiContingencyPlans)
      setInitialContingencyPlanIds(
        extractContingencyPlanIds(apiContingencyPlans)
      )
    }
  }, [apiContingencyPlans, isConsentFuturePlanEnabled, isSnapshot])

  useEffect(() => {
    if (
      isConsentRiskLinkEnabled &&
      !isReviewingRef.current &&
      !isSnapshot &&
      Array.isArray(apiRiskAssessments)
    ) {
      setSelectedRiskAssessments(apiRiskAssessments)
      setInitialRiskAssessmentIds(apiRiskAssessments)
    }
  }, [apiRiskAssessments, isConsentRiskLinkEnabled, isSnapshot])

  // One-time init for status and next review date
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!consentForm || isSnapshot) return

    if (!isInitialized.current) {
      // Initialize selectedCarePlans from consentForm and save initial IDs
      if (consentForm.carePlans?.length) {
        setSelectedCarePlans(consentForm.carePlans)
        // Store initial care plan IDs to detect newly linked plans later
        const initialIds = consentForm.carePlans.map(
          (cp: string | CarePlanAdlExtended) =>
            typeof cp === 'string' ? cp : cp.id
        )
        setInitialCarePlanIds(initialIds)
      }

      // Initialize consentStatus from consentForm
      if (consentForm.consentStatus) {
        setConsentStatus({
          status: consentForm.consentStatus,
          reason: consentForm.statusReason,
          lastReviewDate: consentForm.lastReviewed as unknown as string
        })
      }
      if (consentForm.nextReviewDate) {
        setNextReviewDate(
          dayjs(consentForm.nextReviewDate) as unknown as string
        )
      }

      isInitialized.current = true
    }
  }, [consentForm, isSnapshot])

  const onSubmitSign = async ({ formState, onSuccess }: any) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    try {
      await onSubmit({
        resident,
        nextReviewDate,
        formState,
        consentSingleForm: consentForm,
        isSingleSign: true,
        item: signatureItem.id,
        category: signatureItem.category,
        status: consentStatus.status
      })
      setIsSubmitting(false)
      onSuccess()
      fetchResidentConsentFormByID(consentFormIdFromURL)
    } catch {
      setIsSubmitting(false)
    }
  }

  const hasChangedRiskAssessments =
    JSON.stringify([...selectedRiskAssessments].sort()) !==
    JSON.stringify([...initialRiskAssessmentIds].sort())

  const handleSave = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    try {
      const res = await updateConsent({
        isRefused: consentStatus.status === ConsentStatus.refused,
        comment: additionalInfo,
        editedBy: resident.id,
        editedOn: dayjs(new Date()).toISOString(),
        nextReviewDate: nextReviewDate
          ? dayjs(nextReviewDate).toISOString()
          : null,
        consentForm,
        consentStatus: consentStatus.status,
        carePlans: extractCarePlanIds(selectedCarePlans),
        ...(isConsentFuturePlanEnabled
          ? {
              contingencyPlans: extractContingencyPlanIds(
                selectedContingencyPlans
              )
            }
          : {}),
        // Only include riskAssessments when the user actually changed them,
        // so unrelated saves don't risk overwriting server-side links if our
        // local state never picked them up (e.g. response missing the field).
        ...(isConsentRiskLinkEnabled && hasChangedRiskAssessments
          ? { riskAssessments: selectedRiskAssessments }
          : {}),
        statusReason: consentStatus.reason,
        lastReviewed: consentStatus.lastReviewDate
      })
      if (res) {
        // Invalidate care-plans cache to refresh snapshot data
        queryClient.invalidateQueries('care-plans')
        if (isConsentFuturePlanEnabled) {
          queryClient.invalidateQueries('contingencyPlans')
        }
        if (isConsentRiskLinkEnabled) {
          queryClient.invalidateQueries('risk-assessments')
        }

        // Filter to only newly linked care plans (not in initial set)
        const newlyLinked = selectedCarePlans.filter(
          (cp): cp is CarePlanAdlExtended => {
            if (typeof cp === 'string') return false
            return !initialCarePlanIds.includes(cp.id)
          }
        )
        const newlyLinkedContingency = selectedContingencyPlans.filter(
          (plan): plan is ContingencyPlanDTO => {
            if (typeof plan === 'string') return false
            return !initialContingencyPlanIds.includes(plan.id)
          }
        )

        // Update initialCarePlanIds to include all currently selected care plans
        setInitialCarePlanIds(extractCarePlanIds(selectedCarePlans))
        setInitialContingencyPlanIds(
          extractContingencyPlanIds(selectedContingencyPlans)
        )
        setInitialRiskAssessmentIds(selectedRiskAssessments)

        if (newlyLinked.length > 0 || newlyLinkedContingency.length > 0) {
          setNewlyLinkedCarePlans(newlyLinked)
          setNewlyLinkedContingencyPlans(newlyLinkedContingency)
          setShowCarePlanLinkingModal(true)
        } else {
          handleCancel()
        }
      }
    } catch (e) {
      console.log(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    setIsReviewing(false)
    setAdditionalInfo(undefined)
    setNewlyLinkedCarePlans([])
    setNewlyLinkedContingencyPlans([])
  }

  const handleDateChange = (date: any, _dateString: any) => {
    if (date !== null) {
      setNextReviewDate(date)
    } else {
      setTimeOptionNextReview(undefined)
      setNextReviewDate(undefined)
    }
  }

  const isDesktop = isBrowser || isTablet

  const nextReviewFieldState = getNextReviewFieldState(consentStatus.status)

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (nextReviewFieldState.isDisabled && isReviewing) {
      setNextReviewDate(undefined)
      setTimeOptionNextReview(undefined)
      form.setFieldValue('nextReviewDate', undefined)
    }
  }, [nextReviewFieldState.isDisabled, isReviewing, form])

  // Use snapshot data if available, otherwise use consentForm
  const displayData =
    isSnapshot && snapshotData
      ? {
          id: snapshotData.id,
          title: snapshotData.title || 'Untitled',
          body: snapshotData.body || '',
          isArchived: snapshotData.isArchived || false,
          consentStatus: snapshotData.consentStatus as ConsentStatus,
          nextReviewDate: snapshotData.nextReviewDate,
          carePlans: []
        }
      : consentForm

  // Transform snapshot logs to ensure they have the structure LogTimelineRow expects
  const transformSnapshotLogs = (snapshotLogs: any[]) => {
    return snapshotLogs.map((log) => {
      // Find the carer from Redux store if loggedBy is just an ID
      let carerName = 'Carer' // Default fallback
      if (typeof log.loggedBy === 'string') {
        const carer = carers?.find((c: any) => c.id === log.loggedBy)
        if (carer) {
          carerName = `${carer.name.first} ${carer.name.last}`
        }
      }

      return {
        ...log,
        // Add carerName field that LogTimelineRow expects
        carerName,
        // Populate categoryColor from the existing category.color
        categoryColor: log.categoryColor || log.category?.color || '#6D92EB',
        // Keep the existing category and item (backend already sends these with icons)
        category: log.category || {
          id: '',
          icon: '',
          color: '#6D92EB'
        },
        item: log.item || {
          id: '',
          icon: ''
        }
      }
    })
  }

  // Use snapshot logs if available, otherwise use regular logs
  const displayLogs =
    isSnapshot && snapshotData?.logs
      ? transformSnapshotLogs(snapshotData.logs)
      : logs

  // Always need resident data
  if (!resident) return null

  // For regular consent forms, need displayData
  if (!isSnapshot && !displayData) return null

  // For snapshots, need snapshotData
  if (isSnapshot && !snapshotData) return null

  const isNextReviewDateInvalid =
    nextReviewFieldState.isRequired && !nextReviewDate
  const isConfirmDisabled = getIsConsentConfirmDisabled({
    ...consentStatus,
    disabled: isNextReviewDateInvalid
  })

  const pdfUrl = `residents/${resident?.id}/pdf/consent/${displayData?.id}`
  const url = `/api/services/pdf/export?url=${encodeURIComponent(pdfUrl)}`

  return (
    <div style={printPDF ? styles.pdfWrapper : styles.wrapper}>
      <Row style={styles.mainRow as CSSProperties}>
        <Row>{printPDF && <PDFHeader home={home} resident={resident} />}</Row>
        <Row>
          <div style={styles.header}>
            <PageHeader
              sticky
              title={displayData?.title}
              onBack={
                !printPDF
                  ? () => {
                      if (from === 'carePlan') {
                        navigate(-1)
                      } else {
                        navigate('../', { relative: 'path' })
                      }
                    }
                  : undefined
              }
            >
              <ButtonsWrapper>
                {consentStatus.status !== ConsentStatus.noStatus && (
                  <ConsentStatusTag
                    status={consentStatus.status}
                    label={getStatusLabel(consentStatus.status)}
                  />
                )}
                {!isSnapshot && !isReviewing ? (
                  <Tooltip
                    title={
                      displayData?.isArchived
                        ? t(
                            `consent:${consentConstants.CANNOT_REVIEW_ARCHIVED}`
                          )
                        : ''
                    }
                  >
                    <Button
                      variant="primary"
                      onClick={() => setIsReviewing(true)}
                      disabled={displayData?.isArchived}
                      data-testid="consent-form-review-btn"
                    >
                      {t(`general.buttons.review`)}
                    </Button>
                  </Tooltip>
                ) : (
                  !isSnapshot && (
                    <InReviewControllsWrapper>
                      <Button
                        variant="default"
                        style={{ marginRight: 10 }}
                        onClick={() => handleCancel()}
                        data-testid="consent-form-cancel-btn"
                      >
                        {t(`general.buttons.cancel`)}
                      </Button>
                      <Button
                        disabled={isConfirmDisabled || isSubmitting}
                        isLoading={isSubmitting}
                        onClick={() =>
                          isSignStatus
                            ? setShowSignatureModal(true)
                            : handleSave()
                        }
                        variant="primary"
                        style={{ marginRight: 10 }}
                        icon={
                          !isSubmitting ? (
                            <Icon name="CheckOutlined" />
                          ) : undefined
                        }
                        data-testid="consent-form-confirm-btn"
                      >
                        {isSignStatus ? t('sign') : t(`general.buttons.save`)}
                      </Button>
                    </InReviewControllsWrapper>
                  )
                )}
                {!printPDF && !isSnapshot && (
                  <PdfExport
                    pdfTitle={`${resident?.name?.first} ${resident?.name?.last}'s ${displayData?.title}`}
                    url={url}
                    text={
                      isDesktop
                        ? translate('downloadPDF')
                        : translate('downloadPDFMobile')
                    }
                  />
                )}
              </ButtonsWrapper>
            </PageHeader>
          </div>
          {!isSnapshot && (
            <FullWidthContainer>
              <CarePlanSelector
                variant="consent"
                residentId={resident?.id}
                selectedCarePlans={selectedCarePlans}
                onChange={setSelectedCarePlans}
                readonly={!isReviewing}
                label={
                  <LabelContainer>
                    <Text level={1} weight="bold">
                      {translate('linkedCarePlans')}
                    </Text>
                    <Text level={2}>{translate('consentFormsWillAppear')}</Text>
                  </LabelContainer>
                }
              />
              {isConsentFuturePlanEnabled && (
                <ContingencyPlanSelector
                  residentId={resident?.id}
                  selectedContingencyPlans={selectedContingencyPlans}
                  onChange={setSelectedContingencyPlans}
                  readonly={!isReviewing}
                  label={
                    <LabelContainer>
                      <Text level={1} weight="bold">
                        {translate('linkedFuturePlans')}
                      </Text>
                      <Text level={2}>
                        {translate('consentFormsWillAppearInFuturePlans')}
                      </Text>
                    </LabelContainer>
                  }
                />
              )}
              {isConsentRiskLinkEnabled && (
                <RiskAssessmentSelector
                  variant="consent"
                  residentId={resident?.id}
                  selectedRiskAssessments={selectedRiskAssessments}
                  onChange={setSelectedRiskAssessments}
                  readonly={!isReviewing}
                  label={
                    <LabelContainer>
                      <Text level={1} weight="bold">
                        {translate('linkedRiskAssessments')}
                      </Text>
                      <Text level={2}>
                        {translate('consentFormsWillAppearInRiskAssessments')}
                      </Text>
                    </LabelContainer>
                  }
                />
              )}
            </FullWidthContainer>
          )}
          <FullWidthContainer>
            <Mb24></Mb24>
          </FullWidthContainer>

          {isReviewing && !isSnapshot && (
            <FullWidthContainer>
              <ConsentConfirmationCard
                onStatusChange={setConsentStatus}
                selectedStatus={consentStatus}
              />
              <Mb24 />
              <Form form={form}>
                <NextReviewField
                  disabled={nextReviewFieldState.isDisabled}
                  localisation={{
                    title: 'Next review',
                    additionalInfo: 'Any additional comments on this review?'
                  }}
                  dateValue={(nextReviewDate as string) || undefined}
                  datePickerOnChange={handleDateChange}
                  radioGroupOnChange={setTimeOptionNextReview}
                  timeValue={timeOptionNextReview}
                  timeOptions={getTimeOptions(translate)}
                  isMobile={!isDesktop}
                />
              </Form>
            </FullWidthContainer>
          )}

          <ConsentFormTextBox
            styles={{ marginTop: 0 }}
            consentForm={displayData as ConsentForm}
            localisation={{
              title: displayData?.title || '',
              subTitle: translate('statement', {
                variable: resident?.preferredName || resident?.name?.first
              }),
              readOnly: labelTextBoxTranslate('readOnly')
            }}
            resident={resident}
          />
        </Row>
        {!isReviewing && !isSnapshot && (
          <NextReviewCard
            nextReviewDate={
              (displayData?.nextReviewDate || nextReviewDate) as string
            }
            style={styles.reviewCard}
            translate={translate}
          />
        )}

        <LogsSection
          consentForm={displayData as ConsentForm}
          isSubmitting={isSubmitting}
          logs={displayLogs}
          resident={resident}
          translate={translate}
        />
        <br />
      </Row>
      {!printPDF && (
        <Row>
          <LabelIcon
            iconColor={colors.pink}
            iconName="QuestionCircleOutlined"
            onPress={() =>
              window.open(
                'http://support.logmycare.co.uk/en/articles/6014582-how-do-i-review-a-consent-or-lack-of-capacity-form',
                '_blank'
              )
            }
            text="Help"
            textColor={colors.pink}
          />
        </Row>
      )}
      {!isSnapshot && (
        <SignatureModal
          resident={resident}
          footerLabel={displayData?.title}
          confirmText={translate('confirm', {
            variable: resident?.preferredName || resident?.name?.first
          })}
          isShowing={showSignatureModal}
          isSubmitting={isSubmitting}
          onSubmit={({ formState }: any) => {
            onSubmitSign({
              formState,
              onSuccess: () => {
                setShowSignatureModal(false)
                handleSave()
              }
            })
          }}
          onClose={() => setShowSignatureModal(false)}
        />
      )}
      {!isSnapshot && (
        <CarePlanLinkingConfirmationModal
          isModalVisible={showCarePlanLinkingModal}
          setIsModalVisible={setShowCarePlanLinkingModal}
          newlyLinkedCarePlans={newlyLinkedCarePlans}
          newlyLinkedContingencyPlans={newlyLinkedContingencyPlans}
          onConfirm={handleCancel}
        />
      )}
    </div>
  )
}

const styles = {
  header: {
    width: '100%',
    position: 'sticky',
    top: 0,
    zIndex: 10
  } as React.CSSProperties,
  mainRow: {
    flexDirection: 'column'
  },
  reviewCard: {
    marginTop: 24
  },
  wrapper: {
    maxWidth: 1080,
    padding: '0px 0px 100px',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  pdfWrapper: {
    position: 'absolute' as any,
    top: 0,
    left: 0,
    width: 1190,
    minHeight: 1480,
    zIndex: 1000,
    background: 'white',
    padding: '24px 28px'
  }
}

const FullWidthContainer = styled.div`
  width: 100%;
`

const LabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`

const Mb24 = styled.div`
  margin-bottom: 24px;
`

const InReviewControllsWrapper = styled.div`
  display: flex;
`

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`
